import React, { useState, useRef, useEffect } from 'react';
import { usePurchases } from '@/hooks/use-currency';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDistanceToNow } from 'date-fns';
import { 
  Package, 
  AlertCircle, 
  Calendar, 
  ShoppingBag, 
  Play, 
  Pause, 
  Box, 
  Search, 
  Filter,
  X,
  Music,
  Flag,
  ImageIcon,
  StickyNote,
  User,
  ArrowUpDown,
  Check,
  Loader2,
  Volume2,
  LayoutDashboard,
  ExternalLink
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { motion } from 'framer-motion';
import { useLocation } from 'wouter';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function UserPurchases() {
  const { data: purchases, isLoading, isError } = usePurchases();
  const { toast } = useToast();
  const [_, setLocation] = useLocation();
  const [playingAudio, setPlayingAudio] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // New state for search and filtering
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [showActive, setShowActive] = useState<boolean>(true);
  const [showInactive, setShowInactive] = useState<boolean>(true);
  const [sortOption, setSortOption] = useState<string>("newest");
  const [filteredItems, setFilteredItems] = useState<any[]>([]);

  // Add loading state for audio preview
  const [audioLoading, setAudioLoading] = useState<number | null>(null);

  // Data processing is handled in the usePurchases hook
  const purchaseItems = purchases || [];

  // Apply filters and search
  useEffect(() => {
    if (!purchaseItems || purchaseItems.length === 0) {
      setFilteredItems([]);
      return;
    }

    let result = [...purchaseItems];

    // Apply active/inactive filter
    result = result.filter(({ purchase }) => {
      const isActive = purchase && purchase.isActive !== undefined ? purchase.isActive : true;
      return (isActive && showActive) || (!isActive && showInactive);
    });

    // Apply category filter
    if (selectedCategory !== "all") {
      result = result.filter(({ item }) => item.itemType === selectedCategory);
    }

    // Apply search term filter
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(({ item }) => 
        item.name.toLowerCase().includes(lowercasedSearch) || 
        (item.description && item.description.toLowerCase().includes(lowercasedSearch))
      );
    }

    // Apply sorting
    switch (sortOption) {
      case "newest":
        result.sort((a, b) => {
          const dateA = a.purchase?.purchaseDate ? new Date(a.purchase.purchaseDate).getTime() : 0;
          const dateB = b.purchase?.purchaseDate ? new Date(b.purchase.purchaseDate).getTime() : 0;
          return dateB - dateA;
        });
        break;
      case "oldest":
        result.sort((a, b) => {
          const dateA = a.purchase?.purchaseDate ? new Date(a.purchase.purchaseDate).getTime() : 0;
          const dateB = b.purchase?.purchaseDate ? new Date(b.purchase.purchaseDate).getTime() : 0;
          return dateA - dateB;
        });
        break;
      case "a-z":
        result.sort((a, b) => a.item.name.localeCompare(b.item.name));
        break;
      case "z-a":
        result.sort((a, b) => b.item.name.localeCompare(a.item.name));
        break;
      default:
        // Default sorting is newest first
        result.sort((a, b) => {
          const dateA = a.purchase?.purchaseDate ? new Date(a.purchase.purchaseDate).getTime() : 0;
          const dateB = b.purchase?.purchaseDate ? new Date(b.purchase.purchaseDate).getTime() : 0;
          return dateB - dateA;
        });
    }

    setFilteredItems(result);
  }, [purchaseItems, selectedCategory, searchTerm, sortOption, showActive, showInactive]);

  // Get unique categories from items
  const getCategories = () => {
    if (!purchaseItems || purchaseItems.length === 0) return [];
    
    const categories = new Set<string>();
    
    purchaseItems.forEach(({ item }) => {
      if (item.itemType) {
        categories.add(item.itemType);
      }
    });
    
    return Array.from(categories);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3
      }
    }
  };
  
  // Stop audio playback when component unmounts
  React.useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);
  
  // Play or pause audio preview
  const toggleAudioPreview = (item: any, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering card click
    
    // Stop current audio if it's playing
    if (audioRef.current) {
      audioRef.current.pause();
      
      // If we're clicking the same item that's already playing, just stop it
      if (playingAudio === item.id) {
        setPlayingAudio(null);
        return;
      }
    }
    
    // Create new audio element if we don't have one
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    
    // Set the new source and play (only for sound items)
    // Find the correct audio URL - use audioUrl if available, fall back to imageUrl
    const audioUrl = item.audioUrl || item.imageUrl;
    
    if (audioUrl) {
      try {
        setAudioLoading(item.id);
        
        // Set up event listeners
        audioRef.current.onloadeddata = () => {
          setAudioLoading(null);
        };
        
        audioRef.current.onended = () => {
          setPlayingAudio(null);
        };
        
        audioRef.current.onerror = () => {
          setAudioLoading(null);
          setPlayingAudio(null);
          toast({
            title: "Error",
            description: "Could not load sound preview",
            variant: "destructive"
          });
        };
        
        // Set source and play
        audioRef.current.src = audioUrl;
        audioRef.current.volume = 0.5;
        
        audioRef.current.play()
          .then(() => {
            setPlayingAudio(item.id);
            setAudioLoading(null);
          })
          .catch(err => {
            console.error('Error playing audio:', err);
            setPlayingAudio(null);
            setAudioLoading(null);
            toast({
              title: "Error",
              description: "Failed to play audio preview",
              variant: "destructive"
            });
          });
      } catch (err) {
        console.error('Error setting up audio:', err);
        setPlayingAudio(null);
        setAudioLoading(null);
      }
    } else {
      toast({
        title: "No Audio",
        description: "This item doesn't have audio to preview",
        variant: "default"
      });
    }
  };

  // Handle item click based on item type
  const handleItemClick = (item: any) => {
    if (item.itemType === 'board') {
      // Navigate to board details page
      setLocation(`/board/${item.id}`);
    }
    // Add other item type handlers here if needed
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center mb-4">
          <Box className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold ml-2">Your Purchases</h2>
        </div>
        
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="border border-gray-800 bg-black/40 rounded-lg p-5">
              <div className="flex justify-between items-center mb-3">
                <Skeleton className="h-7 w-40 bg-gray-800" />
                <Skeleton className="h-6 w-20 bg-gray-800 rounded-full" />
              </div>
              <Skeleton className="h-4 w-full bg-gray-800 mb-4" />
              <Skeleton className="h-20 w-full bg-gray-800 rounded-lg mb-3" />
              <Skeleton className="h-4 w-40 bg-gray-800" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <div className="flex items-center mb-4">
          <Box className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold ml-2">Your Purchases</h2>
        </div>
        
        <div className="rounded-md bg-red-900/20 border border-red-700 p-4 text-red-200 text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          <span>Failed to load your purchases</span>
        </div>
      </div>
    );
  }

  const categories = getCategories();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Box className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold">Your Purchases</h2>
        </div>
        
        <Badge variant="outline" className="bg-primary/10 border-primary/20 text-xs">
          {filteredItems.length} of {purchaseItems.length} {purchaseItems.length === 1 ? 'item' : 'items'}
        </Badge>
      </div>

      {/* Search and Filter Controls */}
      <div className="flex flex-col space-y-3">
        <div className="flex items-center space-x-2">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search your purchases..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-gray-800 border-gray-700 focus:border-primary h-9"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <X className="h-4 w-4 text-gray-400 hover:text-white" />
              </button>
            )}
          </div>
          
         
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="w-24 bg-gray-800 border-gray-700 space-x-1 h-9">
                <ArrowUpDown className="h-4 w-4" />
                <span>Sort</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-gray-800 border-gray-700">
              <DropdownMenuItem onClick={() => setSortOption("newest")}>
                <div className={`flex items-center justify-between w-full ${sortOption === "newest" ? "text-primary" : ""}`}>
                  <span>Newest First</span>
                  {sortOption === "newest" && <Check className="h-4 w-4" />}
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOption("oldest")}>
                <div className={`flex items-center justify-between w-full ${sortOption === "oldest" ? "text-primary" : ""}`}>
                  <span>Oldest First</span>
                  {sortOption === "oldest" && <Check className="h-4 w-4" />}
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOption("a-z")}>
                <div className={`flex items-center justify-between w-full ${sortOption === "a-z" ? "text-primary" : ""}`}>
                  <span>A to Z</span>
                  {sortOption === "a-z" && <Check className="h-4 w-4" />}
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOption("z-a")}>
                <div className={`flex items-center justify-between w-full ${sortOption === "z-a" ? "text-primary" : ""}`}>
                  <span>Z to A</span>
                  {sortOption === "z-a" && <Check className="h-4 w-4" />}
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Category filter buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => setSelectedCategory("all")}
            variant={selectedCategory === "all" ? "default" : "outline"}
            size="sm"
            className={`rounded-full h-8 ${selectedCategory === "all" ? "" : "bg-gray-800 border-gray-700"}`}
          >
            All Items
          </Button>
          
          {categories.map((category) => {
            let icon;
            let displayName = category.charAt(0).toUpperCase() + category.slice(1);
            
            if (category === 'sound') {
              icon = <Music className="h-3 w-3 mr-1" />;
            } else if (category === 'banner') {
              icon = <Flag className="h-3 w-3 mr-1" />;
            } else if (category === 'emote') {
              icon = <StickyNote className="h-3 w-3 mr-1" />;
            } else if (category === 'avatar') {
              icon = <User className="h-3 w-3 mr-1" />;
            } else if (category === 'board') {
              icon = <LayoutDashboard className="h-3 w-3 mr-1" />;
            } else {
              icon = <Package className="h-3 w-3 mr-1" />;
            }
            
            return (
              <Button
                key={category}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                className={`rounded-full h-8 flex items-center ${
                  selectedCategory === category ? "" : "bg-gray-800 border-gray-700"
                }`}
              >
                {icon}
                {displayName}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Filter results summary */}
      {(searchTerm || selectedCategory !== "all" || !showActive || !showInactive) && (
        <div className="text-xs text-gray-400">
          Showing {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'} 
          {searchTerm ? ` matching "${searchTerm}"` : ''}
          {selectedCategory !== "all" ? ` in ${selectedCategory}` : ''}
          {!showActive || !showInactive ? ' (filtered by status)' : ''}
        </div>
      )}

      {!purchaseItems || purchaseItems.length === 0 ? (
        <div className="text-center p-8 border border-gray-700 rounded-lg bg-gradient-to-br from-black/60 to-gray-900/20">
          <ShoppingBag className="h-10 w-10 mx-auto mb-3 text-gray-500" />
          <p className="text-gray-400 font-medium">You haven't made any purchases yet</p>
          <p className="text-xs text-gray-500 mt-1">Items you purchase will appear here</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center p-8 border border-gray-700 rounded-lg bg-gradient-to-br from-black/60 to-gray-900/20">
          <Search className="h-10 w-10 mx-auto mb-3 text-gray-500" />
          <p className="text-gray-400 font-medium">No items match your filters</p>
          <p className="text-xs text-gray-500 mt-1">Try adjusting your search or filters</p>
        </div>
      ) : (
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          {filteredItems.map(({ purchase, item }) => {
            // Add null check in case purchase is undefined or doesn't have required properties
            const isActive = purchase && purchase.isActive !== undefined ? purchase.isActive : true;
            const purchaseDate = purchase && purchase.purchaseDate ? purchase.purchaseDate : new Date().toISOString();
            const expiryDate = purchase && purchase.expiryDate ? purchase.expiryDate : null;
            
            return (
              <motion.div 
                key={purchase && purchase.id ? purchase.id : `item-${item.id}`} 
                variants={itemVariants}
              >
                <Card 
                  className={`overflow-hidden border-gray-800 bg-gradient-to-br from-green-900/10 to-black/80 h-full flex flex-col ${item.itemType === 'board' ? 'cursor-pointer hover:border-primary/50 hover:shadow-md' : ''}`}
                  onClick={() => item.itemType === 'board' ? handleItemClick(item) : undefined}
                >
                  <CardHeader className="pb-2 p-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base text-white">
                        {item.name}
                      </CardTitle>
                      
                    </div>
                    <CardDescription className="text-gray-300 text-xs line-clamp-2">
                      {item.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="p-3 pt-0 flex-grow">
                    {item.imageUrl && (
                      <div className="w-full h-24 bg-gray-800/60 rounded-md mb-2 overflow-hidden relative">
                        <img 
                          src={item.imageUrl} 
                          alt={item.name} 
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Sound type label */}
                        {item.itemType === 'sound' && (
                          <Badge className="absolute top-1 left-1 bg-blue-900/80 text-blue-100 border-none text-xs">
                            Sound
                          </Badge>
                        )}

                        {/* Item type badge for non-sound items */}
                        {item.itemType && item.itemType !== 'sound' && (
                          <Badge className="absolute top-1 left-1 bg-gray-900/80 text-gray-100 border-none text-xs">
                            {item.itemType.charAt(0).toUpperCase() + item.itemType.slice(1)}
                          </Badge>
                        )}

                        {/* For board items, add a view details indicator */}
                        {item.itemType === 'board' && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity">
                            <span className="text-white text-xs font-medium flex items-center gap-1">
                              <ExternalLink className="h-3 w-3" /> View Details
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 text-xs text-gray-400 mt-2">
                      <Calendar className="h-3 w-3" />
                      <span>Purchased {formatDistanceToNow(new Date(purchaseDate), { addSuffix: true })}</span>
                    </div>
                    
                    {expiryDate && (
                      <div className="mt-1 text-xs text-gray-400">
                        <span>Expires: </span>
                        <span className="text-gray-300">{formatDistanceToNow(new Date(expiryDate), { addSuffix: true })}</span>
                      </div>
                    )}
                  </CardContent>
                  
                  {/* Add a proper footer with preview button for sound items */}
                  {item.itemType === 'sound' && (
                    <CardFooter className="p-3 pt-1 flex justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        className={`w-full ${playingAudio === item.id ? 'bg-primary/20 border-primary text-primary' : 'bg-gray-800/80 border-gray-700 hover:bg-gray-700'}`}
                        onClick={(e) => toggleAudioPreview(item, e)}
                        disabled={audioLoading === item.id}
                      >
                        {audioLoading === item.id ? (
                          <div className="flex items-center gap-2 justify-center">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            <span>Loading...</span>
                          </div>
                        ) : playingAudio === item.id ? (
                          <div className="flex items-center gap-2 justify-center">
                            <Pause className="h-3 w-3" />
                            <span>Stop Preview</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 justify-center">
                            <Play className="h-3 w-3" />
                            <span>Play Sound</span>
                          </div>
                        )}
                      </Button>
                    </CardFooter>
                  )}

                  {/* Add view details button for board items */}
                  {item.itemType === 'board' && (
                    <CardFooter className="p-3 pt-1 flex justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full bg-gray-800/80 border-gray-700 hover:bg-primary/20 hover:border-primary/30"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleItemClick(item);
                        }}
                      >
                        <ExternalLink className="h-3 w-3 mr-2" />
                        View Board Details
                      </Button>
                    </CardFooter>
                  )}
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}